import * as React from 'react';
import { currentUrl, getBreakpoint, relativeUrl, URLX, latestPageRenderWasOnTheServer } from '@avensia/excite';
import {} from '@avensia/excite/latest-render-was-on-the-server';
import { getRequestContext } from '@avensia/excite/server/request-context';

function useFastLoadImageUrls() {
  const fastLoadImageUrls = __BROWSER__ ? window?.FAST_LOAD_IMAGES || [] : getRequestContext().fastLoadImages || [];
  return fastLoadImageUrls;
}

export function useShouldEagerLoadImage(url: string | undefined, forceEagerLoading?: boolean): boolean {
  const fastLoadImageUrls = useFastLoadImageUrls();
  if (!url) {
    return true;
  }

  if (forceEagerLoading) {
    return forceEagerLoading;
  }
  return url ? Boolean(fastLoadImageUrls.find(i => new URLX(i.url).pathname === new URLX(url).pathname)) : false;
}

export function imageIsPreloaded() {
  const latestRenderWasOnServer = latestPageRenderWasOnTheServer();
  const initialWidthisMobile = getBreakpoint() == 'mobile';
  return latestRenderWasOnServer && initialWidthisMobile;
}

export type ImageUrlsType = { src: string | undefined; srcSet: string | undefined };

export enum ImageResizeMode {
  Pad = 'pad',
}

export type ImageUrlOptions = {
  height?: number;
  searchParams?: URLSearchParams;
  resizeMode?: ImageResizeMode;
  preset?: string;
  quality?: number;
  width?: number;
  external?: boolean;
  forceEagerLoading?: boolean;
  imageUrlResolver?: (src: string, density?: number, options?: ImageUrlOptions) => string;
};

export const isSvg = (url: string) => /.*\.svg$/.test(url);
export const isBase64Svg = (url: string) => /^(data:image\/svg\+xml)/.test(url);

export function placeholderSrc(width?: number | string, height?: number | string): string {
  return `data:image/svg+xml,${encodeURI(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"></svg>`,
  )}`;
}

export function useImageUrls(
  url: string | undefined,
  width: number,
  ref: React.RefObject<HTMLImageElement | null>,
  options?: ImageUrlOptions,
): ImageUrlsType {
  const isPreloaded = imageIsPreloaded();
  const fastLoadImageUrls = useFastLoadImageUrls();
  const fastLoadImageUrl = fastLoadImageUrls.find(i => new URLX(i.url).pathname === url)?.url;
  let srcSet: string | undefined;

  if (!url) {
    return { src: undefined, srcSet: undefined };
  }

  const internalImageUrl = options?.imageUrlResolver || imageUrl;
  const src = isPreloaded ? fastLoadImageUrl || internalImageUrl(url, 2, options) : internalImageUrl(url, 2, options);

  if (!(isSvg(url) || isBase64Svg(url)) && !options?.external) {
    srcSet = isPreloaded && fastLoadImageUrl ? undefined : imageSrcSet(url, width, options);
  }

  return { src, srcSet };
}

export function imageUrl(src: string, density?: number, options?: ImageUrlOptions): string {
  if (!src || options?.external) {
    return src;
  }
  const url = new URLX(src);

  if (!(isBase64Svg(src) || isSvg(url.pathname))) {
    if (options?.width) {
      url.searchParams.set(
        'w',
        String(density ? (options?.preset ? options.width : options.width * density) : options.width),
      );
    }
    if (options?.height) {
      url.searchParams.set('h', String(density ? options.height * density : options.height));
    }
    if (options?.resizeMode) {
      url.searchParams.set('fit', String(options.resizeMode));
    }
    if (options?.preset) {
      url.searchParams.set('preset', options?.preset);
    }
    if (options?.preset && density && density > 1) {
      url.searchParams.set('dpr', String(density));
    }
    if (options?.quality) {
      url.searchParams.set('q', String(options?.quality));
    }
  }

  options?.searchParams?.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  return relativeUrl(url, currentUrl());
}

const DENSITY_DESCRIPTORS = [1, 1.5, 2];
function imageSrcSet(src: string, width: number, options?: ImageUrlOptions): string {
  const internalImageUrl = options?.imageUrlResolver || imageUrl;
  const urls = DENSITY_DESCRIPTORS.map(density => {
    const url = internalImageUrl(src, density, options);
    if (url) {
      return `${url} ${width * density}w`;
    }
  }).filter(url => !!url);
  return urls.join();
}
